/// <reference types="facebook-js-sdk" />
import type { App, InjectionKey } from "vue";
import { useScriptTag } from "@vueuse/core";
// src/plugins/facebook-whatsapp.ts
import { inject } from "vue";

export interface FacebookWhatsAppOptions {
  appId: string;
  configId: string;
  version?: string;
}

export interface WhatsAppConnectResponse {
  type: "WA_EMBEDDED_LINK";
  /** O código de autorização retornado pelo Facebook */
  code: string;
  /** Outros dados enviados no evento */
  [key: string]: unknown;
}

// src/types/whatsapp-embedded-signup.ts

/** Eventos possíveis do fluxo Embedded Signup/Link */
export type WhatsAppEmbeddedSignupEvent =
  | "FINISH"
  | "FINISH_ONLY_WABA"
  | "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING"
  | "CANCEL";

/** Formato base de todas as respostas */
interface BaseResponse {
  type: "WA_EMBEDDED_SIGNUP";
  event: WhatsAppEmbeddedSignupEvent;
}

/** Fluxo concluído com sucesso */
export interface WhatsAppSignupSuccess extends BaseResponse {
  event: "FINISH" | "FINISH_ONLY_WABA" | "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING";
  data: {
    /** ID do número de telefone WhatsApp Business */
    phone_number_id: string;
    /** ID da WhatsApp Business Account */
    waba_id: string;
    /** ID do portfólio de negócios */
    business_id: string;
  };
}

/** Fluxo abandonado (sem erro), apenas informa em qual etapa o usuário parou */
export interface WhatsAppSignupAbandon extends BaseResponse {
  event: "CANCEL";
  data: {
    /** Tela onde o usuário abandonou o fluxo */
    current_step: string;
  };
}

/** Fluxo de erro reportado pelo usuário */
export interface WhatsAppSignupError extends BaseResponse {
  event: "CANCEL";
  data: {
    /** Mensagem de erro apresentada ao usuário */
    error_message: string;
    /** Código do erro (para suporte) */
    error_id: string;
    /** ID único da sessão (para suporte) */
    session_id: string;
    /** Timestamp Unix de quando o erro foi reportado */
    timestamp: string;
  };
}

/** União de todas as respostas possíveis */
export type WhatsAppEmbeddedSignupResponse =
  | WhatsAppSignupSuccess
  | WhatsAppSignupAbandon
  | WhatsAppSignupError;

export interface FacebookWhatsAppService {
  /**
   * Carrega e inicializa o SDK do Facebook
   */
  init: () => Promise<void>;
  /**
   * Dispara o fluxo de conexão WhatsApp Business
   * e retorna os dados da mensagem postMessage do iframe
   */
  connect: () => Promise<WhatsAppEmbeddedSignupResponse>;
}

const FB_WHATSAPP_KEY: InjectionKey<FacebookWhatsAppService> = Symbol("fbWhatsapp");

// NÃO é assim que detectamos abandono do fluxo: abandono é sinalizado pelo
// próprio Embedded Signup via mensagem `event: "CANCEL"`, já tratada no call
// site (src/pages/agents/deploy/whatsapp/WhasappDeployCard.tsx). Este timer é
// só uma rede de segurança para não vazar o listener de "message" pra sempre
// caso o Facebook nunca poste nada e o usuário também nunca dispare CANCEL
// (ex.: fecha a aba sem sinal explícito).
const LISTENER_CLEANUP_TIMEOUT_MS = 10 * 60 * 1000;

export default {
  install(app: App, options: FacebookWhatsAppOptions) {
    const { appId, configId, version = "v22.0" } = options;
    let initPromise: Promise<void> | undefined;
    let isReady = false;

    function init(): Promise<void> {
      if (initPromise) return initPromise;

      initPromise = new Promise<void>((resolve, reject) => {
        if (isReady) return resolve();
        globalThis.fbAsyncInit = () => {
          try {
            globalThis.FB!.init({
              appId,
              autoLogAppEvents: true,
              xfbml: false,
              version,
            });
            resolve();
          } catch (e) {
            reject(e);
          }
        };
        const { load } = useScriptTag("https://connect.facebook.net/en_US/sdk.js", undefined, {
          attrs: { async: "", defer: "", crossorigin: "anonymous" },
        });
        load(true)
          .then(() => {
            isReady = true;
          })
          .catch(reject);
      });
      return initPromise;
    }

    async function connect(): Promise<any> {
      if (!isReady) {
        await init();
      }

      return new Promise((resolve, reject) => {
        console.debug("Conectando WhatsApp Business");
        // abre o diálogo FB.login
        globalThis.FB!.login(() => {}, {
          config_id: configId,
          response_type: "code",
          override_default_response_type: true,
          extras: {
            setup: {},
            featureType: "<FEATURE_TYPE>",
            sessionInfoVersion: "3",
          },
        });
        // aguarda mensagem do iframe
        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        const handler = (ev: MessageEvent) => {
          console.debug("Mensagem recebida do iframe", ev);
          if (!ev.origin.includes("facebook.com")) return;

          try {
            const data = JSON.parse(ev.data) as WhatsAppEmbeddedSignupResponse;
            if (data.type === "WA_EMBEDDED_SIGNUP") {
              clearTimeout(timeoutId);
              window.removeEventListener("message", handler);
              resolve(data);
            }
          } catch {
            // ignorar
            console.warn("Mensagem não é um JSON válido", ev.data);
          }
        };

        window.addEventListener("message", handler);
        timeoutId = setTimeout(() => {
          window.removeEventListener("message", handler);
          reject(new Error("Timeout ao conectar WhatsApp"));
        }, LISTENER_CLEANUP_TIMEOUT_MS);
      });
    }

    const service: FacebookWhatsAppService = { init, connect };
    app.provide(FB_WHATSAPP_KEY, service);
  },
};

export function useFacebookWhatsApp(): FacebookWhatsAppService {
  const svc = inject(FB_WHATSAPP_KEY);
  if (!svc) throw new Error("Plugin Facebook WhatsApp não instalado");
  return svc;
}
